import { Divider, Stack } from 'component-library';
import { FC, HTMLAttributes } from 'react';
import { useTranslation } from 'react-i18next';
import { NameType, ValueType } from 'recharts/types/component/DefaultTooltipContent';

import { TrendLabel as TrendLabelEnum } from '@/api/rest/resources/conservation';
import { PlotReportFactLabelNameEnum, THREAT_TYPES, ThreatType } from '@/api/rest/resources/types/fact';
import { INDICATOR } from '@/api/rest/resources/types/IndicatorProduct';
import { UnitEnum } from '@/api/rest/resources/types/units';
import { ChartProps, GraphFact } from '@/components/Charts/types';
import { getDisplayNumber } from '@/hooks/useDisplayNumber';
import { usePlotGroupFilterStartYear } from '@/hooks/usePlotGroupFilterStartYear';
import { Logger } from '@/lib/logs/logger';
import {
  RenderTrendLine,
  TrendLabel,
} from '@/pages/shared/projects/pages/project/pages/conservation/shared/components/ReferenceLine';
import { colorPalette } from '@/theme/colorPalette';
import { isNullish } from '@/utils/booleans';
import { printYear } from '@/utils/formatting/date';
import { getColorForIndicator } from '@/utils/getColorForIndicator';
import { getColorForThreat } from '@/utils/getColorForThreat';

import { Bar, Chart, ComposedChart, getXAxisProps, TooltipProps } from './components/Chart';
import { ChartLegendBar } from './components/LegendBar';
import { ChartTooltip, ChartTooltipTextPrimary, ChartTooltipTextSecondary } from './components/Tooltip';
import { ChartLegendBarItem } from './components/types';
import { NoChartData } from './NoChartData';
import { getChartDataFromGraphFact, getXAxisDomainForHistoricGraph, sortChartData } from './utils';

const factNames = {
  threat_breakdown_total: 'threat_breakdown_total',
};

export const THREATS_SUM_KEY = 'threatsSum';

const UNIT = UnitEnum['%'];

const THREATS_BREAKDOWN_LABEL = 'shared.ncaDetail.details.habitatIntactness.threatsBreakdown.labels.threatsBreakdown';

const threatsBreakdownColors = getColorForIndicator(INDICATOR.HABITAT_INTACTNESS_THREATS_BREAKDOWN);

const SHARED_BAR_PROPS = {
  stackId: 'threats',
  isAnimationActive: true,
  barSize: 21,
  stroke: threatsBreakdownColors.stroke,
  strokeWidth: 1,
};

const CHART_COLORS = {
  [ThreatType.builtup]: { fill: getColorForThreat(ThreatType.builtup) },
  [ThreatType.deforestation]: { fill: getColorForThreat(ThreatType.deforestation) },
  [ThreatType.cropland]: { fill: getColorForThreat(ThreatType.cropland) },
  [ThreatType.roads]: { fill: getColorForThreat(ThreatType.roads) },
  [ThreatType.no_threat_area]: { fill: getColorForThreat(ThreatType.no_threat_area) },
  [factNames.threat_breakdown_total]: { fill: threatsBreakdownColors.fill, stroke: threatsBreakdownColors.stroke },
  trendLabel: { stroke: '#004c3d' },
} as const;

export type HabitatIntactnessThreatsBreakdownChart = HTMLAttributes<HTMLDivElement> & {
  trendLabel?: TrendLabelEnum | null;
  plotGroupId?: string;
} & ChartProps;

export const HabitatIntactnessThreatsBreakdownHistorical: FC<HabitatIntactnessThreatsBreakdownChart> = ({
  data,
  trendLabel,
  plotGroupId,
  ...delegated
}) => {
  const filtersStartYear = usePlotGroupFilterStartYear(plotGroupId);

  const orderedThreatTypes = [
    ...THREAT_TYPES.filter((type) => type !== ThreatType.no_threat_area),
    ThreatType.no_threat_area, // Force no threat area to be on top of the graph
  ];

  const threatsData = Object.fromEntries(
    orderedThreatTypes
      .map((type) => {
        const threatData = data.filter((series) =>
          series.labels?.some((l) => l.name === PlotReportFactLabelNameEnum.threat_type && l.value === type),
        );

        return [type, threatData] as const;
      })
      .filter(([, threatData]) => threatData.length > 0),
  );

  const threatsDataMap = Object.fromEntries(
    Object.entries(threatsData).map(([type, facts]) => [type, getChartDataFromGraphFact(facts, factNames)]),
  );

  const sortedThreatsData = Object.fromEntries(
    Object.entries(threatsDataMap).map(([type, threatData]) => [
      type,
      sortChartData(threatData, (date) => new Date(date).getTime()),
    ]),
  );

  const dates = Array.from(
    new Set(Object.values(threatsDataMap).flatMap((threatData) => Object.keys(threatData))),
  ).sort((dateA, dateB) => new Date(dateA).getTime() - new Date(dateB).getTime());

  const firstTick = dates.at(0);
  const lastTick = dates.at(-1);

  if (!firstTick || !lastTick) {
    Logger.error('Not enough data to render the habitat intactness historical chart');
    return (
      <div className='h-full justify-center' {...delegated}>
        <NoChartData />
      </div>
    );
  }

  const chartData = dates.map((date) => {
    const dataPoint: Record<string, number | Record<ThreatType, GraphFact>> = {
      label: new Date(date).getTime(),
    };

    let threatsSum = 0;

    Object.entries(sortedThreatsData).forEach(([threatType, threatData]) => {
      const threatDataForLabel = threatData.find((d) => d.label === dataPoint.label);
      if (threatDataForLabel) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        dataPoint[`${threatType}`] = (threatDataForLabel as any)?.[factNames.threat_breakdown_total];
        threatsSum +=
          threatType !== ThreatType.no_threat_area
            ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
              ((threatDataForLabel as any)?.[factNames.threat_breakdown_total]?.value ?? 0)
            : 0;
      }
    });

    dataPoint[`${THREATS_SUM_KEY}`] = threatsSum;

    return dataPoint;
  });

  const initialYear = new Date(firstTick).getFullYear();

  const xTicksDomain = getXAxisDomainForHistoricGraph(firstTick, lastTick, filtersStartYear ?? initialYear, true);

  const xAxisProps = {
    ...getXAxisProps(xTicksDomain),
    tickFormatter: printYear,
  };

  return (
    <>
      <Chart.Container
        {...delegated}
        data-testid='habitat-intactness-threats-breakdown-chart'
        data={chartData}
        config={{
          [factNames.threat_breakdown_total]: {
            color: '', // Note: this is not required, since we render a custom legend, but the color key is required
          },
          [THREATS_SUM_KEY]: {
            color: '',
          },
        }}
      >
        <ComposedChart data={chartData}>
          {Object.keys(sortedThreatsData).map((threat) => (
            <Bar
              key={threat}
              dataKey={`${threat}.value`}
              name={threat}
              fill={CHART_COLORS[`${threat as ThreatType}`].fill}
              className={`threat-type-bar-${threat}`}
              {...SHARED_BAR_PROPS}
            />
          ))}
          {trendLabel &&
            RenderTrendLine({
              dataKey: THREATS_SUM_KEY,
              stroke: CHART_COLORS.trendLabel.stroke,
              label: (props) =>
                props.index === chartData.length - 1 && trendLabel ? (
                  <TrendLabel
                    trend={trendLabel}
                    lineColor={CHART_COLORS.trendLabel.stroke}
                    strokeWidth={2}
                    {...props}
                  />
                ) : (
                  <></>
                ),
              dotFill: CHART_COLORS.trendLabel.stroke,
            })}
          <Chart.XAxis {...xAxisProps} padding={{ left: 40, right: 100 }} className='threats-breakdown-x-axis' />
          <Chart.YAxis domain={[0, 100]} className='threats-breakdown-y-axis'>
            <Chart.Label>{UNIT}</Chart.Label>
          </Chart.YAxis>
          <Chart.Tooltip cursor={{ stroke: colorPalette.divider, strokeWidth: 1 }} content={CustomTooltip} />
        </ComposedChart>
      </Chart.Container>
      <CustomLegendBar trendLabel={trendLabel} threats={Object.keys(sortedThreatsData) as ThreatType[]} />
    </>
  );
};

const CustomLegendBar = ({ trendLabel, threats }: { trendLabel?: TrendLabelEnum | null; threats: ThreatType[] }) => {
  const { t } = useTranslation();

  const legendBarItems: ChartLegendBarItem[] = threats.map((threat) => ({
    label: t(`global.threatTypes.${threat}`),
    // eslint-disable-next-line security/detect-object-injection
    color: CHART_COLORS[threat].fill,
    shape: 'column',
  }));

  if (trendLabel) {
    legendBarItems.push({
      label: t('shared.ncaDetail.habitatIntactness.evolutionBU.trend'),
      color: CHART_COLORS.trendLabel.stroke,
      shape: 'line',
    });
  }

  return (
    <ChartLegendBar
      items={legendBarItems}
      className='sm:ml-[28px]'
      data-testid='habitat-intactness-threats-breakdown-chart-legend'
    />
  );
};

const CustomTooltip = ({ active, payload, label }: TooltipProps<ValueType, NameType>) => {
  const { t } = useTranslation();

  if (!active || !payload?.length) return null;

  const getThreatDataFromPayload = (threat: ThreatType) => {
    const threatData = payload?.find(({ dataKey }) => dataKey === `${threat}.value`);

    if (isNullish(threatData?.value) || typeof threatData?.value !== 'number') {
      return null;
    }

    const threatDisplayValue = `${getDisplayNumber(threatData.value.toString(), window.navigator.language)} ${UNIT}`;

    if (!threatDisplayValue) return null;

    return {
      key: threat,
      display: threatDisplayValue,
      value: threatData.value,
      text: t(`global.threatTypes.${threat}`),
    };
  };

  const displayThreats = THREAT_TYPES.filter((threat) => threat !== ThreatType.no_threat_area).flatMap(
    (threat) => getThreatDataFromPayload(threat) ?? [],
  );
  const noThreatArea = getThreatDataFromPayload(ThreatType.no_threat_area);

  const sumOfThreatsData = displayThreats.reduce<number>((acc, threat) => acc + threat.value, 0);
  const sumOfThreatsDisplay = !isNullish(sumOfThreatsData)
    ? `${getDisplayNumber(sumOfThreatsData.toString(), window.navigator.language)} ${UNIT}`
    : t('global.analysis.noData');

  return (
    <ChartTooltip className='ml-1'>
      <Stack spacing={8} direction='row' className='justify-between'>
        <ChartTooltipTextSecondary>{printYear(label)}</ChartTooltipTextSecondary>
      </Stack>
      <Stack spacing={2} direction='col'>
        <Stack direction='row' spacing={8} className='justify-between'>
          <ChartTooltipTextPrimary>{t(THREATS_BREAKDOWN_LABEL)}</ChartTooltipTextPrimary>
          <ChartTooltipTextPrimary>{sumOfThreatsDisplay}</ChartTooltipTextPrimary>
        </Stack>
        <Divider />
        {displayThreats.map(({ key, display, text }, i) => (
          <div key={key}>
            <Stack direction='row' spacing={8} className='justify-between'>
              <ChartTooltipTextSecondary>{text}</ChartTooltipTextSecondary>
              <ChartTooltipTextSecondary>{display}</ChartTooltipTextSecondary>
            </Stack>
            {i !== displayThreats.length - 1 && <Divider className='mt-2' />}
          </div>
        ))}
        {noThreatArea && (
          <div>
            <Divider className='mb-2' />
            <Stack direction='row' spacing={8} className='justify-between'>
              <ChartTooltipTextPrimary>{t(`global.threatTypes.${noThreatArea.key}`)}</ChartTooltipTextPrimary>
              <ChartTooltipTextPrimary>{noThreatArea.display}</ChartTooltipTextPrimary>
            </Stack>
          </div>
        )}
      </Stack>
    </ChartTooltip>
  );
};

export { CustomTooltip as ThreatsBreakdownHistoricalCustomTooltip };
